import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import IndustryIcon from '../components/IndustryIcon';
import { useLanguage } from '../contexts/LanguageContext';
import {
  FIVEM_SOLUTION,
  getIndustryCommonCopy,
  getLocalizedValue,
  getHomeIndustries,
} from '../data/industries';

const IndustriesIndexContent = () => {
  const { currentLang, isRTL } = useLanguage();
  const copy = getIndustryCommonCopy(currentLang);
  const industries = getHomeIndustries().map((industry) =>
    getLocalizedValue(industry, currentLang)
  );
  const fivem = getLocalizedValue(FIVEM_SOLUTION, currentLang);

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <section className="relative overflow-hidden bg-black pt-20">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 py-20 md:px-[7.6923%] md:py-28">
          <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
            {copy.indexEyebrow}
          </p>
          <h1 className="max-w-5xl text-4xl font-semibold leading-tight text-white md:text-6xl">
            {copy.indexTitle}
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-8 text-white/60 md:text-lg">
            {copy.indexSubtitle}
          </p>
        </div>
      </section>

      <section className="bg-[#050706] py-16 md:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-5 px-5 md:grid-cols-2 md:px-[7.6923%] xl:grid-cols-4">
          {industries.map((industry) => (
            <Link
              key={industry.id}
              to={industry.route}
              className="group rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#00FFD1]/30 hover:bg-[#00FFD1]/[0.035]"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-[#00FFD1]/25 bg-[#00FFD1]/10 text-[#00FFD1]">
                <IndustryIcon icon={industry.icon} />
              </span>
              <h2 className="text-xl font-semibold leading-snug text-white">
                {industry.card.title}
              </h2>
              <p className="mt-3 min-h-[96px] text-sm leading-6 text-white/55">
                {industry.card.summary}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#00FFD1]">
                {copy.viewIndustry}
                <ArrowRight
                  className={`h-4 w-4 transition-transform ${
                    isRTL
                      ? 'rotate-180 group-hover:-translate-x-1'
                      : 'group-hover:translate-x-1'
                  }`}
                />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-[7.6923%]">
          <div className="rounded-3xl border border-[#00FFD1]/25 bg-[#00FFD1]/[0.045] p-6 md:flex md:items-center md:justify-between md:gap-8">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#00FFD1] text-black">
                <IndustryIcon icon={fivem.icon} />
              </span>
              <div>
                <h2 className="text-2xl font-semibold text-white">
                  {fivem.card.title}
                </h2>
                <p className="mt-2 text-sm leading-6 text-white/60">
                  {fivem.card.summary}
                </p>
              </div>
            </div>
            <Link
              to={fivem.route}
              className="btn-glass-primary mt-5 w-full md:mt-0 md:w-auto"
            >
              {copy.fivemCta}
              <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const IndustriesPage = () => (
  <PublicPageShell>
    <IndustriesIndexContent />
  </PublicPageShell>
);

export default IndustriesPage;
